import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { PawPrint, CalendarPlus, CalendarCheck, Clock, ChevronRight } from 'lucide-react';

export default function ClientDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pets, setPets] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [petsRes, apptsRes] = await Promise.all([
          api.get('/pets'),
          api.get('/bookings'),
        ]);
        setPets(petsRes.data);
        setAppointments(apptsRes.data);
      } catch {}
      setLoading(false);
    };
    fetchData();
  }, []);

  const upcoming = appointments.filter(a => ['pending', 'confirmed'].includes(a.status)).slice(0, 5);

  const statusColor = (s) => {
    const map = { pending: 'bg-amber-100 text-amber-800', confirmed: 'bg-blue-100 text-blue-800', completed: 'bg-green-100 text-green-800', cancelled: 'bg-red-100 text-red-800' };
    return map[s] || 'bg-gray-100 text-gray-800';
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#2D2D2D] border-t-transparent" /></div>;

  return (
    <div className="space-y-8" data-testid="client-dashboard">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: 'Manrope' }}>
          Welcome back, {user?.name?.split(' ')[0]}
        </h1>
        <p className="text-sm text-[#5A5A5A] mt-1">Here's what's happening with your pets</p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: CalendarPlus, label: 'Book Appointment', path: '/client/book', color: '#2D2D2D' },
          { icon: PawPrint, label: 'Add Pet', path: '/client/pets/new', color: '#4A7C59' },
          { icon: CalendarCheck, label: 'My Appointments', path: '/client/appointments', color: '#4A90E2' },
          { icon: Clock, label: 'View Invoices', path: '/client/invoices', color: '#E07A5F' },
        ].map((action) => (
          <Card key={action.path} className="rounded-2xl border-[#E5E5E0] bg-white card-hover cursor-pointer group" onClick={() => navigate(action.path)} data-testid={`quick-action-${action.label.toLowerCase().replace(/\s/g, '-')}`}>
            <CardContent className="p-5 flex flex-col items-center text-center gap-3">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-[1.05]" style={{ background: `${action.color}15`, transition: 'transform 0.3s ease' }}>
                <action.icon className="w-6 h-6" style={{ color: action.color }} strokeWidth={1.5} />
              </div>
              <span className="text-sm font-medium text-[#2D2D2D]">{action.label}</span>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* My Pets */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-[#1A1A1A]" style={{ fontFamily: 'Manrope' }}>My Pets</h2>
          <Button variant="ghost" className="text-sm text-[#5A5A5A]" onClick={() => navigate('/client/pets')} data-testid="view-all-pets-btn">
            View All <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
        {pets.length === 0 ? (
          <Card className="rounded-2xl border-[#E5E5E0] bg-white border-dashed">
            <CardContent className="p-8 text-center">
              <PawPrint className="w-10 h-10 text-[#888] mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-sm text-[#5A5A5A] mb-4">No pets added yet. Add your first pet to get started!</p>
              <Button className="rounded-full bg-[#2D2D2D] text-white" onClick={() => navigate('/client/pets/new')} data-testid="add-first-pet-btn">
                Add Pet
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {pets.slice(0, 3).map((pet) => (
              <Card key={pet.pet_id} className="rounded-2xl border-[#E5E5E0] bg-white card-hover cursor-pointer" onClick={() => navigate(`/client/pets/${pet.pet_id}`)} data-testid={`pet-card-${pet.pet_id}`}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-[#F5F5F0] rounded-2xl flex items-center justify-center">
                      <PawPrint className="w-7 h-7 text-[#2D2D2D]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-[#2D2D2D]">{pet.name}</h3>
                      <p className="text-sm text-[#5A5A5A]">{pet.breed || pet.species}</p>
                      <p className="text-xs text-[#888] mt-0.5">{pet.gender} {pet.weight ? `| ${pet.weight}kg` : ''}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Upcoming Appointments */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-[#1A1A1A]" style={{ fontFamily: 'Manrope' }}>Upcoming Appointments</h2>
          <Button variant="ghost" className="text-sm text-[#5A5A5A]" onClick={() => navigate('/client/appointments')} data-testid="view-all-appointments-btn">
            View All <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
        {upcoming.length === 0 ? (
          <Card className="rounded-2xl border-[#E5E5E0] bg-white border-dashed">
            <CardContent className="p-8 text-center">
              <CalendarCheck className="w-10 h-10 text-[#888] mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-sm text-[#5A5A5A] mb-4">No upcoming appointments</p>
              <Button className="rounded-full bg-[#2D2D2D] text-white" onClick={() => navigate('/client/book')} data-testid="book-first-appointment-btn">
                Book Now
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {upcoming.map((appt) => (
              <Card key={appt.appointment_id} className="rounded-2xl border-[#E5E5E0] bg-white card-hover" data-testid={`appointment-card-${appt.appointment_id}`}>
                <CardContent className="p-5 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#F5F5F0] rounded-xl flex items-center justify-center">
                      <CalendarCheck className="w-6 h-6 text-[#2D2D2D]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#2D2D2D]">{appt.date} at {appt.time_slot}</p>
                      <p className="text-xs text-[#5A5A5A]">&#8377;{appt.total_price}</p>
                    </div>
                  </div>
                  <span className={`text-xs font-medium px-3 py-1 rounded-full ${statusColor(appt.status)}`}>
                    {appt.status}
                  </span>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
